import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Typography,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Button
} from '@mui/material';
import {
  formatRoute,
  formatDateTime,
  getAircraftLabel,
  getPilotNames,
  renderApprovalChips,
  getStatusColor,
  formatActorName,
  formatStatusHistoryDateTime,
  canEditSectorAction,
  canCancelSectorAction
} from './SalesEnquiryUtils';
import { formatDateTimeWithTimezone } from '../../utils/timezoneUtils';
import WorkflowStatusCard from '../common/WorkflowStatusCard';
import QuickAuditTrail from '../common/QuickAuditTrail';
import RemarksHistory from '../common/RemarksHistory';

const SectorTabsView = ({ sectors, selectedTrip, aircraft, user, displayTimezone, onEditSector, onCancelSector, onEmergencyGroundSector }) => {
  const [selectedSectorTab, setSelectedSectorTab] = useState(0);
  const [sectorDetailTabValues, setSectorDetailTabValues] = useState({});

  // Reset to first tab when sectors change
  useEffect(() => {
    setSelectedSectorTab(0);
    setSectorDetailTabValues({});
  }, [sectors?.length, selectedTrip?._id]);

  const handleSectorTabChange = (event, newValue) => {
    setSelectedSectorTab(newValue);
    // Initialize detail tab for the selected sector if not already set
    const sectorId = sectors[newValue]?._id;
    if (sectorId && sectorDetailTabValues[sectorId] === undefined) {
      setSectorDetailTabValues((prev) => ({
        ...prev,
        [sectorId]: 0
      }));
    }
  };

  const handleSectorDetailTabChange = (sectorId, newValue) => {
    setSectorDetailTabValues((prev) => ({
      ...prev,
      [sectorId]: newValue
    }));
  };

  if (!sectors || sectors.length === 0) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" align="center">
          No sectors found for this trip
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Tabs
        value={selectedSectorTab}
        onChange={handleSectorTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {sectors.map((sector, index) => (
          <Tab
            key={sector._id}
            label={`Sector ${sector.seqNo} - ${formatRoute(sector)}${
              sector.departure?.date ? ` · ${formatDateTime(sector.departure.date)}` : ''
            }`}
            icon={
              <Chip
                label={sector.status}
                color={getStatusColor(sector.status)}
                size="small"
                sx={{ ml: 1 }}
              />
            }
            iconPosition="end"
            sx={{
              backgroundColor: selectedSectorTab === index ? 'rgba(25, 118, 210, 0.12)' : 'transparent',
              '&.Mui-selected': {
                backgroundColor: 'rgba(25, 118, 210, 0.16)',
                fontWeight: 'bold'
              },
              '&:hover': {
                backgroundColor: selectedSectorTab === index ? 'rgba(25, 118, 210, 0.20)' : 'rgba(0, 0, 0, 0.04)'
              }
            }}
          />
        ))}
      </Tabs>

      {/* Sector Content */}
      {sectors[selectedSectorTab] && (() => {
        const sector = sectors[selectedSectorTab];
        const sectorDetailTab = sectorDetailTabValues[sector._id] ?? 0;

        return (
          <Box>
            {/* Sector Summary Card */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Route
                    </Typography>
                    <Typography variant="body1">{formatRoute(sector)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Departure
                    </Typography>
                    <Typography variant="body1">
                      {displayTimezone
                        ? formatDateTimeWithTimezone(sector.departure.date, sector.departure.time, displayTimezone)
                        : formatDateTime(sector.departure.date, sector.departure.time)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Arrival
                    </Typography>
                    <Typography variant="body1">
                      {displayTimezone
                        ? formatDateTimeWithTimezone(sector.arrival.estimatedDate, sector.arrival.estimatedTime, displayTimezone)
                        : formatDateTime(sector.arrival.estimatedDate, sector.arrival.estimatedTime)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Aircraft
                    </Typography>
                    <Typography variant="body1">{getAircraftLabel(sector.aircraftId, aircraft)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Pilots
                    </Typography>
                    <Typography variant="body1">{getPilotNames(sector.assignedPilots)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Passengers
                    </Typography>
                    <Typography variant="body1">
                      {(() => {
                        const sectorPassengers = sector.passengers?.length || 0;
                        const tripPassengers = selectedTrip.passengers?.length || 0;
                        return sectorPassengers > 0 ? sectorPassengers : tripPassengers;
                      })()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Weight
                    </Typography>
                    <Typography variant="body1">
                      {sector.totalWeight ?? 0} kg
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Chip
                      label={sector.status}
                      color={getStatusColor(sector.status)}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Wake Turbulence
                    </Typography>
                    <Typography variant="body1">{sector.wakeTurbulence || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Flight Rules
                    </Typography>
                    <Typography variant="body1">{sector.flightRules || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Itinerary notes & ground handling
                    </Typography>
                    <Typography variant="body2">
                      <strong>Passenger notes:</strong> {sector.itineraryNotes || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Ground transfer:</strong> {sector.groundTransferNotes || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Dep. transport:</strong> {sector.departureTransport?.vehicleDetails || '—'}
                      {' · '}
                      <strong>Arr. transport:</strong> {sector.arrivalTransport?.vehicleDetails || '—'}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Dep. handler:</strong>{' '}
                      {sector.departureGroundHandler?.contactPerson
                        ? `${sector.departureGroundHandler.contactPerson}${sector.departureGroundHandler.contactNumber ? ` (${sector.departureGroundHandler.contactNumber})` : ''}`
                        : '—'}
                      {' · '}
                      <strong>Arr. handler:</strong>{' '}
                      {sector.arrivalGroundHandler?.contactPerson
                        ? `${sector.arrivalGroundHandler.contactPerson}${sector.arrivalGroundHandler.contactNumber ? ` (${sector.arrivalGroundHandler.contactNumber})` : ''}`
                        : '—'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Approvals
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {renderApprovalChips(selectedTrip.pendingApprovals, selectedTrip.approvalStatus, `sector-${sector._id}`)}
                    </Stack>
                  </Grid>
                  {(onEditSector || onCancelSector || onEmergencyGroundSector) && (
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {onEditSector && canEditSectorAction(sector, selectedTrip, user) && (
                          <Button variant="outlined" size="small" onClick={() => onEditSector(sector)}>
                            Edit Sector
                          </Button>
                        )}
                        {onCancelSector && canCancelSectorAction(sector, selectedTrip, user) && (
                          <Button variant="outlined" size="small" color="error" onClick={() => onCancelSector(sector)}>
                            Cancel Sector
                          </Button>
                        )}
                        {onEmergencyGroundSector && (() => {
                          const canEmergencyGround = !['CANCELLED', 'COMPLETED', 'CHARTER_COMPLETED', 'GROUNDED'].includes(selectedTrip?.status) &&
                            !['CANCELLED', 'COMPLETED', 'SECTOR_COMPLETED'].includes(sector.status);
                          return canEmergencyGround ? (
                            <Button
                              variant="contained"
                              size="small"
                              color="error"
                              onClick={() => onEmergencyGroundSector(sector)}
                            >
                              Emergency Grounded
                            </Button>
                          ) : null;
                        })()}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Sector Detail Tabs */}
            <Tabs
              value={sectorDetailTab}
              onChange={(e, newValue) => handleSectorDetailTabChange(sector._id, newValue)}
              sx={{ mb: 2 }}
            >
              <Tab label="Workflow Status" />
              <Tab label="Remarks History" />
              <Tab label="Recent Activity" />
            </Tabs>

            {sectorDetailTab === 0 && (
              <WorkflowStatusCard sector={sector} trip={selectedTrip} user={user} />
            )}

            {sectorDetailTab === 1 && (
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Remarks History
                  </Typography>
                  {sector.remarksHistory && sector.remarksHistory.length > 0 ? (
                    <RemarksHistory remarksHistory={sector.remarksHistory} entityType="sector" />
                  ) : (
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                      No remarks yet
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}

            {sectorDetailTab === 2 && (
              <Box>
                <QuickAuditTrail
                  entityType="sector"
                  entityId={sector._id}
                  entityName={`Sector ${sector.seqNo} - ${formatRoute(sector)}`}
                  maxItems={10}
                />
                {sector.statusHistory && sector.statusHistory.length > 0 && (
                  <Card sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle1" gutterBottom>
                        Status History
                      </Typography>
                      <List>
                        {sector.statusHistory
                          .slice()
                          .reverse()
                          .map((entry, idx) => (
                            <ListItem key={idx} sx={{ pl: 0 }}>
                              <Avatar sx={{ 
                                bgcolor: (theme) => {
                                  const color = getStatusColor(entry.status);
                                  return color === 'success' ? theme.palette.success.main :
                                         color === 'error' ? theme.palette.error.main :
                                         color === 'warning' ? theme.palette.warning.main :
                                         color === 'info' ? theme.palette.info.main :
                                         theme.palette.grey[500];
                                }, 
                                width: 32, 
                                height: 32, 
                                mr: 2 
                              }}>
                                {entry.status.charAt(0)}
                              </Avatar>
                              <ListItemText
                                primary={
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Chip
                                      label={entry.status.replace(/_/g, ' ')}
                                      size="small"
                                      color={getStatusColor(entry.status)}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                      {formatStatusHistoryDateTime(entry.changedAt)}
                                    </Typography>
                                  </Box>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      By: {formatActorName(entry.actorId)}
                                    </Typography>
                                    {entry.remarks && (
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ mt: 0.5, fontStyle: 'italic' }}
                                      >
                                        "{entry.remarks}"
                                      </Typography>
                                    )}
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                      </List>
                    </CardContent>
                  </Card>
                )}
              </Box>
            )}
          </Box>
        );
      })()}
    </Box>
  );
};

export default SectorTabsView;
